/* eslint-disable react/prop-types */
import React from 'react';
import DOMPurify from 'dompurify';

/**
 * Sub-Batch 3M (Niranjan, Feb 26 2026) — extracted from
 * GeneralAnalysisEnhancedView.js as part of the conservative refactor.
 *
 * Houses the small visual primitives shared across every panel in the
 * Diversity-Analytics view:
 *
 *   • _sanitizeSvg — DOMPurify wrapper for backend-rendered matplotlib SVGs
 *   • VERDICT_COLORS palette
 *   • VerdictPill (green / yellow / red verdict badge)
 *   • Tag (severity / category pill)
 *   • SectionTitle (Cormorant Garamond italic section heading)
 *   • Caption (italic muted micro-caption under a figure or table)
 *
 * Pure visual extraction: zero behavioural change.  Lines 15-82 of the
 * original GeneralAnalysisEnhancedView.js.
 */

// Renders a backend-generated matplotlib SVG. Server-trusted by design,
// but we still pipe through DOMPurify (SVG profile) so that any future
// backend compromise can't ship a script-bearing SVG to the browser.
// SVG profile allows <text>, <path>, <g>, etc.; strips <script> and
// on* event-handler attributes.
export const _sanitizeSvg = (svg) =>
  DOMPurify.sanitize(svg, { USE_PROFILES: { svg: true, svgFilters: true } });

export const VERDICT_COLORS = {
  green: { bg: '#E6F4EA', fg: '#166737', border: '#3CB371' },
  yellow: { bg: '#FFF4E0', fg: '#B45309', border: '#F4B21A' },
  red: { bg: '#FBEAEA', fg: '#B11F1F', border: '#E04848' },
};

export const VerdictPill = ({ color }) => {
  const c = VERDICT_COLORS[color] || VERDICT_COLORS.yellow;
  return (
    <span
      data-testid={`gae-verdict-${color}`}
      style={{
        background: c.bg,
        color: c.fg,
        border: `1px solid ${c.border}`,
        padding: '4px 12px',
        borderRadius: '999px',
        fontSize: '11px',
        fontWeight: 700,
        textTransform: 'uppercase',
        letterSpacing: '.04em',
      }}
    >
      {color === 'green'
        ? 'Publication-ready'
        : color === 'yellow'
          ? 'Acceptable with caveats'
          : 'Major concerns'}
    </span>
  );
};

export const Tag = ({ children, kind = 'neutral' }) => {
  const palette = {
    high: { bg: '#FBEAEA', fg: '#B11F1F' },
    moderate: { bg: '#FFF4E0', fg: '#B45309' },
    ok: { bg: '#E6F4EA', fg: '#166737' },
    invalid: { bg: '#FBEAEA', fg: '#B11F1F', border: '1px solid #E04848' },
    valid: { bg: '#E6F4EA', fg: '#166737' },
    marginal: { bg: '#FFF4E0', fg: '#B45309' },
    neutral: { bg: '#EEF1EF', fg: '#3F4D44' },
  };
  const c = palette[kind?.toLowerCase()] || palette.neutral;
  return (
    <span
      style={{
        background: c.bg,
        color: c.fg,
        padding: '2px 8px',
        borderRadius: 6,
        fontSize: 11,
        fontWeight: 600,
        border: c.border || '1px solid transparent',
      }}
    >
      {children}
    </span>
  );
};

export const SectionTitle = ({ children }) => (
  <h3
    style={{
      fontFamily: '"Cormorant Garamond", serif',
      fontStyle: 'italic',
      fontWeight: 600,
      fontSize: 22,
      color: '#1F3A2E',
      margin: '24px 0 8px',
    }}
  >
    {children}
  </h3>
);

export const Caption = ({ children }) => (
  <p
    style={{
      fontSize: 12,
      color: '#6B7B72',
      fontStyle: 'italic',
      margin: '4px 0 16px',
    }}
  >
    {children}
  </p>
);
